/**
 * Shop filtering without a full page reload.
 *
 * The filters, sort and pagination are already real links carrying WooCommerce's own
 * query parameters, so this does not invent an endpoint: it fetches the very same URL
 * the link points at, lifts `[data-shop-results]` out of the response and swaps it in.
 * With JS off — or if anything here fails — the links keep working as ordinary
 * navigation, which is why nothing is rewritten server-side.
 *
 * The swap covers the sidebar as well as the grid, so active states, counts and the
 * result total all update together and cannot drift apart.
 *
 * Category links are deliberately NOT intercepted: they move to a different archive
 * with its own hero and breadcrumb, so that is a page navigation, not a filter.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** Links that should filter in place. Product cards inside the grid must not match. */
const AJAX_LINK = '[data-shop-ajax], .pagination a';
/** Below the lg breakpoint the sidebar sits above the grid, so long groups push products down. */
const MOBILE = window.matchMedia('(max-width: 991.98px)');
export default class ShopFilters {
    constructor(root) {
        this.request = null;
        this.root = root;
        this.collapseForMobile();
        // Delegated, so it survives every swap without rebinding.
        this.root.addEventListener('click', (event) => this.onClick(event));
        window.addEventListener('popstate', () => {
            this.load(window.location.href, { push: false, scroll: false });
        });
    }
    /**
     * On mobile the Category group starts closed even on a category archive, where
     * the server opens it (client 29.09). Attribute groups need nothing: the server
     * only opens those with an active pick, and a pick should stay visible.
     */
    collapseForMobile() {
        if (!MOBILE.matches) {
            return;
        }
        this.root.querySelectorAll('details[data-collapse-mobile]').forEach((group) => {
            group.open = false;
        });
    }
    onClick(event) {
        // Let the browser handle modified clicks and anything opening a new tab.
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }
        const target = event.target;
        const link = target === null || target === void 0 ? void 0 : target.closest(AJAX_LINK);
        if (!link || !this.root.contains(link) || link.target === '_blank') {
            return;
        }
        event.preventDefault();
        this.load(link.href, { push: true, scroll: link.matches('.pagination a') });
    }
    load(url, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // A fast series of clicks should only ever render the last one.
            (_a = this.request) === null || _a === void 0 ? void 0 : _a.abort();
            this.request = new AbortController();
            this.root.classList.add('is-loading');
            this.root.setAttribute('aria-busy', 'true');
            try {
                const response = yield fetch(url, {
                    signal: this.request.signal,
                    credentials: 'same-origin',
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                });
                if (!response.ok) {
                    window.location.href = url;
                    return;
                }
                const doc = new DOMParser().parseFromString(yield response.text(), 'text/html');
                const next = doc.querySelector('[data-shop-results]');
                if (!next) {
                    window.location.href = url;
                    return;
                }
                this.root.innerHTML = next.innerHTML;
                this.collapseForMobile();
                if (opts.push) {
                    window.history.pushState({ shopFilters: true }, '', url);
                }
                // Only on pagination: a filter change should leave the viewport alone,
                // but page 2 starting mid-list is disorienting.
                if (opts.scroll) {
                    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
            catch (error) {
                // An aborted request was superseded on purpose; anything else falls back
                // to a normal navigation rather than leaving a half-updated list.
                if ((error === null || error === void 0 ? void 0 : error.name) !== 'AbortError') {
                    window.location.href = url;
                }
            }
            finally {
                this.root.classList.remove('is-loading');
                this.root.removeAttribute('aria-busy');
            }
        });
    }
}
